import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { BuscaPromptService } from '../../services/busca-prompt/busca-prompt.service';
import { AuthService } from '../../services/auth.service';
import { FacetItem, FacetSectionGroup, Prompt } from '../../models/prompt.model';
import { removerStopwords } from '../../shared/text-utils';
import { CadastroPromptService } from '../../services/cadastro-prompt/cadastro-prompt.service';
import { PerfilPromptService } from '../../services/bff-prompts-perfil/prompts-perfil.service';
import { catchError, map, toArray, shareReplay, tap  } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject, of } from 'rxjs';
import { Categoria } from '../../models/categoria.model';
import { MessageNotificationService } from '../../services/message-notification.service';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../constants/validation-messages';
import { sanitizeTag as sanitizeTagUtil } from '../../utils/tag-utils';

@Component({
  selector: 'app-resultados-busca',
  templateUrl: './resultados-busca.component.html',
  styleUrls: ['./resultados-busca.component.css'],
})
export class ResultadosBuscaComponent implements OnInit {
  sanitizeTag = sanitizeTagUtil;
  isGridView: boolean = true;

  activeSecondaryButton: number = 1;

  setActiveSecondaryButton(index: number) {
    this.activeSecondaryButton = index;
    this.isGridView = index === 1;
  }

  loading: boolean = false;
  prompts: Prompt[] = [];
  filtrados: BehaviorSubject<Prompt[]> = new BehaviorSubject<Prompt[]>([]);
  valorBusca: string = '';
  tipoBusca: string = 'relevancia';
  opcoesOrdenacao: any[] = [
    { label: 'Ordenar por...', value: '' },
    { label: 'Relevância', value: 'relevancia' },
    { label: 'Avaliações', value: 'totalAvaliacoes' },
    { label: 'Título', value: 'title' },
    { label: 'Tag', value: 'tags' },
  ];
  paginaAtual: number = 0;
  totalPages: number = 0;
  totalResultados: number = 0;
  tamanhoPagina: number = 9;
  displayedPrompts: Prompt[] = [];
  // Evita payloads gigantes sem necessidade (a tela pagina no client)
  tamanhoBusca: number = 200;
  categorias: Categoria[] = [];
  isFiltroAberto: boolean = false;

  /** Facetas de categoria vindas da API, agrupadas por seção */
  facetCategorias: FacetSectionGroup[] = [];
  /** Facetas brutas da API (para recalcular contagens) */
  private facetCategoriasRaw: FacetSectionGroup[] = [];
  /** Categorias selecionadas via facet (filtros cumulativos) */
  categoriasFacetSelecionadas: string[] = [];

  dadosUsuarioLogado: any;

  private cacheDePerfis = new Map<string, Observable<any>>();

  constructor(
    private router: Router,
    private buscaPromptService: BuscaPromptService,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private cadastroPromptService: CadastroPromptService,
    private userService: PerfilPromptService,
    private messageNotificationService: MessageNotificationService
  ) {}

  ngOnInit() {
    this.dadosUsuarioLogado = this.authService.getUserData();

    this.activatedRoute.queryParams.subscribe((params) => {
      // Compatível com `?search=` (padrão) e `?value=` (links antigos / backend dual)
      const q = params['search'] ?? params['value'] ?? '';
      this.valorBusca = typeof q === 'string' ? q : '';
      this.buscarPrompts();
    });
  }

  buscarPrompts() {
    this.paginaAtual = 0;
    this.startLoading();
    let termoTratado = removerStopwords(this.valorBusca).toLowerCase();
    if (this.valorBusca && (this.tipoBusca === '' || !this.tipoBusca)) {
      this.tipoBusca = 'relevancia';
    }
    if (!this.valorBusca && this.tipoBusca === 'relevancia') {
      this.tipoBusca = 'totalAvaliacoes';
    }

    if (this.valorBusca) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { search: this.valorBusca },
        queryParamsHandling: 'merge',
      });
    }

    const sortParam = this.tipoBusca || 'relevancia';
    this.buscaPromptService
      .searchPrompts(termoTratado, 0, this.tamanhoBusca, sortParam)
      .subscribe({
        next: (res: any) => {
          const loginUsuarioLogado = this.dadosUsuarioLogado?.usuario_login;
          const content = res?.data?.content ?? res?.data?.page?.content ?? res?.content ?? (Array.isArray(res) ? res : []);
          const contentArray = Array.isArray(content) ? content : [];
          const rawFacets = this.normalizarFacetas(res?.data?.facets?.prompt_categoria ?? res?.facets?.prompt_categoria);
          this.facetCategoriasRaw = rawFacets;
          this.categoriasFacetSelecionadas = [];

          this.prompts = contentArray.map((dadosDoPrompt: any) => {
            const ehDono = !!loginUsuarioLogado && loginUsuarioLogado === dadosDoPrompt.login;
            const exibirComoAnonimo = dadosDoPrompt.anonymous && !ehDono;

            const dadosDeExibicao = this.obterDadosDeExibicaoDoAutor(dadosDoPrompt);

            const prompt: Prompt = {
              id: dadosDoPrompt.id,
              title: dadosDoPrompt.title,
              description: dadosDoPrompt.description,
              command: dadosDoPrompt.command,
              login: dadosDoPrompt.login,
              tags: dadosDoPrompt.tags ? dadosDoPrompt.tags.split(',').map((tag: string) => tag.trim().replace(/^#/, '')) : [],
              anonymous: dadosDoPrompt.anonymous,
              totalAvaliacoes: dadosDoPrompt.review ?? 0,
              mediaAvaliacao: dadosDoPrompt.average ?? 0,
              nomeUsuario$: dadosDeExibicao.nome$,
              usuarioAvatar$: dadosDeExibicao.avatar$,
              categories: dadosDoPrompt.categories ?? undefined,
            };
            return prompt;
          });

          this.facetCategorias = this.recalcularContagensFacetas(rawFacets);
          this.filtrados.next(this.prompts);
          this.ordenarPrompts();
          this.atualizarDisplayedPrompts();
          this.stopLoading();
        },
       error: (err: any) => {
        this.prompts = [];
        this.facetCategorias = [];
        this.facetCategoriasRaw = [];
        this.categoriasFacetSelecionadas = [];
        this.filtrados.next([]);

        this.totalResultados = 0;
        this.totalPages = 0;

        this.atualizarDisplayedPrompts();

        const msgErro = err?.error?.message || err?.message || 'Erro desconhecido';
        this.messageNotificationService.showError(
          ERROR_MESSAGES.SEARCH_FAILED,
          err?.status === 0
            ? 'Não foi possível conectar ao servidor. Verifique se o BFF está rodando.'
            : `Falha na busca: ${msgErro}`
        );
        console.error('Erro ao buscar prompts:', err);
        this.stopLoading();
      },
    });
  }

  /**
   * @param dadosDoPrompt
   * @param ehDono
   * @param exibirComoAnonimo
   * @returns
   */
  private obterDadosDeExibicaoDoAutor(
    dadosDoPrompt: any
  ): { nome$: Observable<string>; avatar$: Observable<string> } {

    const loginUsuarioLogado = this.dadosUsuarioLogado?.usuario_login;
    const loginDoAutor = dadosDoPrompt.login;
    const promptEhAnonimo = dadosDoPrompt.anonymous;
    const avatarPadrao = '../../../assets/imagem/avatar3.jpg';
    const ehDono = loginUsuarioLogado && loginUsuarioLogado === loginDoAutor;

    if (ehDono) {
      const nome = `${this.dadosUsuarioLogado.usuario_apelido || loginDoAutor}`;
      const avatarBase64 = this.dadosUsuarioLogado.usuario_base64_arqfoto;
      const avatarUrl = avatarBase64 ? `data:image/png;base64,${avatarBase64}` : avatarPadrao;
      return { nome$: of(nome), avatar$: of(avatarUrl) };
    }

    if (promptEhAnonimo) {
      return { nome$: of('Anônimo'), avatar$: of(avatarPadrao) };
    }

    let perfilDoAutor$ = this.cacheDePerfis.get(loginDoAutor);

    if (!perfilDoAutor$) {
      perfilDoAutor$ = this.userService.getUserData(loginDoAutor).pipe(
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.cacheDePerfis.set(loginDoAutor, perfilDoAutor$);
    }

    const nome$ = perfilDoAutor$.pipe(
      map(perfil => (perfil ? perfil.usuario_apelido : loginDoAutor) || loginDoAutor)
    );

    const avatar$ = perfilDoAutor$.pipe(
      map(perfil => {
        const base64 = perfil ? perfil.usuario_base64_arqfoto : null;
        return base64 ? `data:image/png;base64,${base64}` : avatarPadrao;
      })
    );

    return { nome$, avatar$ };
  }

  atualizarFiltros(event: any) {
    this.categorias = event;
    this.categoriasFacetSelecionadas = [];
    this.filtrarPrompts();
  }

  ordenarPrompts() {
    let base = this.aplicarFiltroFacetInterno(this.prompts);
    const copy = [...base];
    this.ordenar(copy);
    this.filtrados.next(copy);
    this.atualizarListaExibida();
  }

  /** Verifica se o prompt contém a categoria na string categories */
  private promptTemCategoria(p: Prompt, facetValue: string): boolean {
    const cat = (p.categories ?? '').trim();
    if (!cat) return false;
    const categorias = cat.split(',').map(c => c.trim());
    return categorias.includes(facetValue);
  }

  /** Filtra prompts por todas as facetas selecionadas (filtros cumulativos) */
  private aplicarFiltroFacetInterno(lista: Prompt[]): Prompt[] {
    if (this.categoriasFacetSelecionadas.length === 0) return lista;
    return lista.filter(p =>
      this.categoriasFacetSelecionadas.every(fv => this.promptTemCategoria(p, fv))
    );
  }

  /** Toggle ao clicar em um pill de categoria (filtro cumulativo) */
  aplicarFiltroFacet(facetValue: string) {
    const idx = this.categoriasFacetSelecionadas.indexOf(facetValue);
    if (idx >= 0) {
      this.categoriasFacetSelecionadas.splice(idx, 1);
    } else {
      this.categoriasFacetSelecionadas.push(facetValue);
    }
    this.paginaAtual = 0;
    const filtrados = this.aplicarFiltroFacetInterno(this.prompts);
    this.facetCategorias = this.recalcularContagensFacetas(this.facetCategoriasRaw);
    this.ordenarPrompts();
  }

  /** Recalcula contagens das facetas com base nos prompts filtrados pelas seleções atuais */
  private recalcularContagensFacetas(raw: FacetSectionGroup[]): FacetSectionGroup[] {
    if (!raw?.length) return [];
    const base = this.aplicarFiltroFacetInterno(this.prompts);
    return raw.map(g => {
      const groupItems = Array.isArray(g?.items) ? g.items : [];
      return {
        section: g?.section ?? 'Categorias',
        items: groupItems
          .map((item: FacetItem) => ({
            value: item?.value ?? '',
            count: base.filter(p => this.promptTemCategoria(p, item?.value ?? '')).length
          }))
          .filter((item: { value: string; count: number }) => item.value !== '')
      };
    }).filter(g => g.items.length > 0);
  }

  atualizarListaExibida() {
    const res = this.filtrados.getValue();
    this.totalPages = Math.ceil(res.length / this.tamanhoPagina);
    this.totalResultados = res.length;
    const start = this.paginaAtual * this.tamanhoPagina;
    this.displayedPrompts = res.slice(start, start + this.tamanhoPagina);
  }

  private ordenar(prompts: Prompt[]){
    if (this.tipoBusca === 'relevancia') {
      return;
    }
    if (this.tipoBusca === 'totalAvaliacoes') {
      prompts.sort( (a,b) => {
        if((b.totalAvaliacoes ?? 0) == (a.totalAvaliacoes ?? 0)) return (b.mediaAvaliacao ?? 0) - (a.mediaAvaliacao ?? 0)
          else return (b.totalAvaliacoes ?? 0) - (a.totalAvaliacoes ?? 0);
      });
    } else if (
      this.tipoBusca === 'title' ||
      this.tipoBusca === 'description' ||
      this.tipoBusca === 'command'
    ) {
      prompts.sort((a, b) => {
        const valorA = (a[this.tipoBusca as keyof Prompt] ?? '')
          .toString()
          .trim()
          .toLowerCase();
        const valorB = (b[this.tipoBusca as keyof Prompt] ?? '')
          .toString()
          .trim()
          .toLowerCase();
        return valorA.localeCompare(valorB);
      });
    }
    else if (this.tipoBusca === 'tags') {
    prompts.sort((a, b) => {
      const tagA = (a.tags?.[0] || '').toLowerCase();
      const tagB = (b.tags?.[0] || '').toLowerCase();
      return tagA.localeCompare(tagB);
    });
  }

  return prompts;
  }


  filtrarPrompts() {
    this.startLoading();

    if (this.categorias.length > 0) {
      // A API de busca já retorna `categories` como string (descrições).
      // Filtramos localmente por descrição para evitar N+1 chamadas por prompt.
      const selecionadas = new Set(
        this.categorias
          .map((c) => (c?.categoria_descricao ?? '').trim())
          .filter((d) => d !== '')
      );
      const tratados = this.prompts.filter((p) => {
        const cats = (p.categories ?? '')
          .split(',')
          .map((c) => c.trim())
          .filter((c) => c !== '');
        return cats.some((c) => selecionadas.has(c));
      });
      this.filtrados.next(tratados);
      this.stopLoading();
      this.atualizarDisplayedPrompts();
    } else {
      this.ordenarPrompts();
      this.stopLoading();
    }
  }

  private ehSubVetor(principal: any[], sub: any[]) {
    return sub.every(
      (
        (i) => (v) =>
          (i = principal.indexOf(v, i) + 1)
      )(0)
    );
  }

  atualizarDisplayedPrompts() {
    this.atualizarListaExibida();
  }

  onOrdenacaoChange() {
    this.paginaAtual = 0;
    this.ordenarPrompts();
  }

  abrirTelaPrompt(promptId: number) {
    this.router.navigate([`/tela-prompt/${promptId}`]);
  }

  /**
   * Converte <em> do Elasticsearch em span local. Se o backend não enviar highlight
   * (sem hit correspondente), o texto segue exibido sem alteração.
   */
  highlightText(text: string | null | undefined): string {
    if (!text) return text || '';

    const textRegex = new RegExp(`<em>([\\w\\W\\s]+)</em>`, 'gi');
    return text.replace(textRegex, '<span class="highlight">$1</span>');
  }

  verificarUsuarioAnonimo(prompt: any): void {
    const userData = this.authService.getUserData();
    if (prompt.anonymous == true && userData && prompt.login != userData.usuario_login) {
      prompt.login = 'anônimo';
    }
  }

  copiarTexto(prompt: any): void {
    navigator.clipboard.writeText(prompt.command).then(
      () => {
        this.messageNotificationService.showSuccess(
          SUCCESS_MESSAGES.COMMAND_COPIED
        );
        this.copiaPrompt(prompt.id);
      },
      (err) => {
        console.error('Erro ao copiar texto: ', err);
        this.messageNotificationService.showError(
          ERROR_MESSAGES.COPY_FAILED
        );
      }
    );
  }

  irPaginaAnterior() {
    if (this.paginaAtual > 0) {
      this.paginaAtual--;
      this.atualizarDisplayedPrompts();
    }
  }
  irProximaPagina() {
    if (this.paginaAtual < this.totalPages - 1) {
      this.paginaAtual++;
      this.atualizarDisplayedPrompts();
    }
  }

  copiaPrompt(promptId: number) {
    this.cadastroPromptService.contadorExecucaoPrompt(promptId).subscribe({
      next: (res: any) => {},
      error: (err) => {
        console.error('Erro ao copiar prompt:', err);
      },
    });
  }

  getPromptAvatar(usuario: string, anonymous: boolean): Observable<string> {
    if (usuario !== '' && !anonymous) {
      return this.userService.getUserData(usuario).pipe(
        map((userData) => {
          if (
            userData &&
            userData.usuario_base64_arqfoto
          ) {
            return `data:image/png;base64,${userData.usuario_base64_arqfoto}`;
          } else {
            return '../../../assets/imagem/avatar3.jpg';
          }
        }),
        catchError(() => {
          return of('../../../assets/imagem/avatar3.jpg');
        })
      );
    }
    return of('../../../assets/imagem/avatar3.jpg');
  }

  toggleFiltros() {
    this.isFiltroAberto = !this.isFiltroAberto;
  }

  /** Normaliza facetas: aceita formato novo (section + items) ou legado (lista plana). */
  private normalizarFacetas(raw: any): FacetSectionGroup[] {
    if (!raw || !Array.isArray(raw)) return [];
    const primeiro = raw[0];
    if (primeiro?.section != null && Array.isArray(primeiro?.items)) {
      return raw as FacetSectionGroup[];
    }
    if (typeof primeiro?.value === 'string' && typeof primeiro?.count === 'number') {
      return [{ section: 'Categorias', items: raw as FacetItem[] }];
    }
    return [];
  }

  getPromptNome(usuario: string, anonymous: boolean): Observable<string> {
    if (usuario !== '' && !anonymous) {
      return this.userService.getUserData(usuario).pipe(
        map((userData) => {
          if (userData && userData.usuario_apelido) {
            return userData.usuario_apelido;
          } else {
            return 'Anônimo';
          }
        }),
        catchError(() => {
          return of('Anônimo');
        })
      );
    }
    return of('Anônimo');
  }
  //loading
  startLoading(){
    this.loading = true;
  }
  stopLoading(){
    this.loading = false;
  }

}

